import { camelize } from 'casing'
import { PlanType } from '@labrav/react-components'
import { PartnerType } from '../../../types/external'
import type { RootState } from '../../../store/types'
import type { CloudMarketplace, Product } from '../../productsListing/types'
import {
  isAzureContainerProduct,
  mapContainerAzurePlansFromApi,
} from '../../../components/MicrosoftFormComponent/ContainerPlan/containerPlanUtils'
import {
  isAzureVmProduct,
  mapVmAzurePlansFromApi,
} from '../../../components/MicrosoftFormComponent/VmPlan/vmPlanUtils'
import {
  getPricingModel,
  parseDurationFromServiceLength,
} from '../../../utils/amiPrivateOfferUtil'
import { getConvertedGCPPlanDataForOffer } from '../privateOfferCreation/utils/gcpPlanHelper'
import { normalizeAwsRenewalTermsFromApi } from '../privateOfferCreation/utils/installmentHelper'
import { mapSinglePrivateOffer } from './mapPrivateOffer'

export const processSinglePrivateOffer = async ({
  cloudMarketplace,
  response,
  selectedProduct,
  getState,
  partnerType = PartnerType.User,
}: {
  cloudMarketplace: CloudMarketplace
  response: any
  selectedProduct?: Product
  getState: () => RootState
  partnerType?: PartnerType
}) => {
  switch (cloudMarketplace) {
    case 'AZURE': {
      const privateOffer = mapSinglePrivateOffer(
        response?.data,
        'id',
        cloudMarketplace
      )
      const { id, offerStakeholders, plans, ...restPrivateoffer } = privateOffer
      const users = (
        getState().userList[partnerType]?.userList?.users || []
      ).map(({ email }: { email: string }) => email)
      const stakeHolderExisting =
        (offerStakeholders || [])?.filter(
          (item: { email: string }) =>
            !users.length || users.includes(item.email)
        ) || []
      const stakeHolderCustom =
        (offerStakeholders || [])?.filter(
          (item: { email: string }) =>
            users.length > 0 && !users.includes(item.email)
        ) || []
      const mappedPlans = isAzureVmProduct(selectedProduct)
        ? mapVmAzurePlansFromApi({
            plans: plans as Array<Record<string, unknown>>,
            product: selectedProduct,
          })
        : isAzureContainerProduct(selectedProduct)
          ? mapContainerAzurePlansFromApi({
              plans: plans as Array<Record<string, unknown>>,
              product: selectedProduct,
            })
          : plans

      return {
        ...restPrivateoffer,
        plans: mappedPlans,
        offerStakeholders: [...stakeHolderExisting],
        customOfferStakeholders: [...stakeHolderCustom],
      }
    }
    case 'GCP': {
      const {
        links,
        eulaFiles,
        labraOfferId,
        contractDuration,
        paymentType,
        labraPlanId,
        offerMetadata,
        plan: notUsedPlan,
        tcv,
        installmentSchedule,
        individualDiscount,
        ...remainingData
      } = camelize(response?.data)

      const offerPlan = getConvertedGCPPlanDataForOffer(
        response?.data,
        selectedProduct
      )

      return {
        ...remainingData,
        eulaFile: eulaFiles || [],
        privateOfferId: labraOfferId,
        notes: offerMetadata?.notes,
        emailCustomMessage: offerMetadata?.emailCustomMessage,
        ...offerPlan,
      }
    }
    default: {
      const { offerStakeholders, ...remainingData } = camelize(response?.data)
      const users = (
        getState().userList[partnerType]?.userList?.users || []
      ).map(({ email }: { email: string }) => email)
      const stakeHolderExisting =
        (offerStakeholders || [])?.filter(
          (item: { email: string }) =>
            !users.length || users.includes(item.email)
        ) || []
      const stakeHolderCustom =
        (offerStakeholders || [])?.filter(
          (item: { email: string }) =>
            users.length > 0 && !users.includes(item.email)
        ) || []
      return normalizeAwsRenewalTermsFromApi({
        ...remainingData,
        offerStakeholders: [...stakeHolderExisting],
        customOfferStakeholders: [...stakeHolderCustom],
        ...(selectedProduct?.productType === PlanType.AMI
          ? {
              pricingModel: getPricingModel(
                remainingData?.flexiblePaymentTerms,
                remainingData?.containerOfferServiceLength
              ),
              durationType: parseDurationFromServiceLength(
                remainingData?.containerOfferServiceLength
              ).durationType,
              durationLength: parseDurationFromServiceLength(
                remainingData?.containerOfferServiceLength
              ).durationLength,
            }
          : {}),
      })
    }
  }
}
